import React from "react";
import {
  AlertTriangle, CheckCircle2, CircleDot, CirclePause, CircleStop, Clock, Loader2,
  PlugZap, RefreshCw, ShieldAlert, ShieldCheck, WifiOff, XCircle, Zap,
} from "lucide-react";

// One registry for every operational status so the same state reads the same everywhere.
// Tone drives colour; text + icon carry the meaning, so nothing relies on colour alone.
export const STATUS = {
  // Environment
  PAPER: { tone: "neutral", icon: CircleDot, label: "PAPER" },
  SHADOW_LIVE: { tone: "info", icon: CircleDot, label: "SHADOW LIVE" },
  LIVE: { tone: "danger", icon: Zap, label: "LIVE" },
  // Engine / trading
  READY: { tone: "neutral", icon: CircleDot, label: "READY" },
  RUNNING: { tone: "success", icon: Loader2, label: "RUNNING" },
  STOPPED: { tone: "neutral", icon: CircleStop, label: "STOPPED" },
  COMPLETE: { tone: "success", icon: CheckCircle2, label: "COMPLETE" },
  EXITING: { tone: "warning", icon: RefreshCw, label: "EXITING" },
  RECOVERY_PENDING: { tone: "danger", icon: ShieldAlert, label: "RECOVERY PENDING" },
  // AUTO
  AUTO_ON: { tone: "info", icon: Zap, label: "AUTO ON" },
  AUTO_OFF: { tone: "neutral", icon: CirclePause, label: "AUTO OFF" },
  AUTO_EXIT_ACTIVE: { tone: "warning", icon: RefreshCw, label: "AUTO EXIT ACTIVE" },
  AUTO_EXIT_PAUSED: { tone: "warning", icon: CirclePause, label: "AUTO EXIT PAUSED" },
  // Rollover
  ROLL_AVAILABLE: { tone: "info", icon: RefreshCw, label: "ROLL AVAILABLE" },
  ROLLING: { tone: "info", icon: RefreshCw, label: "ROLLING" },
  ROLL_WAITING: { tone: "warning", icon: Clock, label: "ROLL WAITING" },
  ROLL_STOPPED: { tone: "neutral", icon: CircleStop, label: "ROLLOVER STOPPED" },
  ROLL_COMPLETE: { tone: "success", icon: CheckCircle2, label: "ROLL COMPLETE" },
  ROLL_ABORTED: { tone: "danger", icon: XCircle, label: "ROLL ABORTED BY EXIT" },
  // Risk / data
  MARKET_DATA_STALE: { tone: "danger", icon: AlertTriangle, label: "MARKET DATA STALE" },
  PINNED_UNAVAILABLE: { tone: "danger", icon: AlertTriangle, label: "PINNED CONTRACT UNAVAILABLE" },
  CHARGES_UNAVAILABLE: { tone: "warning", icon: AlertTriangle, label: "CHARGES UNAVAILABLE" },
  PARTIAL_PAIR: { tone: "warning", icon: AlertTriangle, label: "PARTIAL PAIR" },
  OUTSIDE_HOURS: { tone: "neutral", icon: Clock, label: "OUTSIDE TRADING HOURS" },
  BROKER_DISCONNECTED: { tone: "danger", icon: WifiOff, label: "BROKER DISCONNECTED" },
  // System
  RECONCILED: { tone: "success", icon: ShieldCheck, label: "RECONCILED" },
  NOT_RECONCILED: { tone: "danger", icon: ShieldAlert, label: "NOT RECONCILED" },
  LIVE_ENABLED: { tone: "danger", icon: PlugZap, label: "LIVE ENABLED" },
  LIVE_DISABLED: { tone: "neutral", icon: PlugZap, label: "LIVE DISABLED" },
};

export const TONES = {
  success: "bg-success-bg text-success-fg border-success-fg/30",
  warning: "bg-warning-bg text-warning-fg border-warning-fg/30",
  danger: "bg-danger-bg text-danger-fg border-danger-fg/30",
  info: "bg-info-bg text-info-fg border-info-fg/30",
  neutral: "bg-neutral-bg text-neutral-fg border-neutral-fg/20",
};

/** `status` is a STATUS key; `label`/`tone`/`icon` override it. `children` append detail text. */
export default function StatusBadge({ status, label, tone, icon, title, className = "", children, ...rest }) {
  const def = STATUS[status] || {};
  const t = tone || def.tone || "neutral";
  const Icon = icon === null ? null : icon || def.icon;
  const text = label ?? def.label ?? status;
  return (
    <span
      role="status"
      aria-label={[text, typeof children === "string" ? children : null, title].filter(Boolean).join(" — ")}
      title={title}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] font-semibold uppercase leading-none tracking-wide ${TONES[t] || TONES.neutral} ${className}`}
      {...rest}
    >
      {Icon && <Icon aria-hidden="true" className="h-3 w-3 shrink-0" />}
      <span>{text}</span>
      {children != null && children !== false && <span className="font-medium normal-case">{children}</span>}
    </span>
  );
}
